import Eyebrow from '../ui/Eyebrow';

function formatNumber(value, digits = 0) {
  if (!Number.isFinite(value)) return '—';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatSigned(value, digits = 0) {
  if (!Number.isFinite(value)) return '—';
  return `${value >= 0 ? '+' : ''}${formatNumber(value, digits)}`;
}

function record(wins = 0, losses = 0, ties = 0) {
  return `${wins}-${losses}${ties ? `-${ties}` : ''}`;
}

function gameLabel(game) {
  if (!game) return 'No recorded game';
  return `${game.year} W${game.week} vs ${game.opponent}`;
}

function Metric({ label, value, note, tone = 'gold' }) {
  const toneClass = tone === 'red' ? 'text-accent-red' : tone === 'plain' ? 'text-ink-primary' : 'text-accent-gold';
  const borderClass = tone === 'red' ? 'border-accent-red' : tone === 'plain' ? 'border-ink-tertiary' : 'border-accent-gold';
  return (
    <article className={`bg-bg-surface border-t-[3px] ${borderClass} p-5 min-w-0`}>
      <div className="text-[9px] tracking-[0.22em] text-ink-tertiary font-extrabold uppercase">{label}</div>
      <div className={`mt-2 font-display text-3xl md:text-4xl font-black uppercase tracking-normal leading-none break-words ${toneClass}`}>
        {value}
      </div>
      {note && <p className="mt-2 text-[12px] text-ink-secondary leading-relaxed italic">{note}</p>}
    </article>
  );
}

function FeatureGame({ label, game, emptyCopy }) {
  if (!game) {
    return (
      <article className="bg-bg-surface border-l-[3px] border-ink-tertiary p-5">
        <div className="text-[9px] tracking-[0.22em] text-ink-tertiary font-extrabold uppercase">{label}</div>
        <p className="mt-3 text-sm text-ink-secondary italic">{emptyCopy}</p>
      </article>
    );
  }
  return (
    <article className={`bg-bg-surface border-l-[3px] ${game.result === 'W' ? 'border-accent-gold' : 'border-accent-red'} p-5`}>
      <div className="text-[9px] tracking-[0.22em] text-ink-tertiary font-extrabold uppercase">{label}</div>
      <div className="mt-2 font-display text-3xl font-black uppercase tracking-normal leading-none">
        {formatNumber(game.score, 2)}–{formatNumber(game.opponentScore, 2)}
      </div>
      <p className="mt-2 text-[12px] text-ink-secondary italic">
        {gameLabel(game)} · {formatSigned(game.margin, 2)} margin
      </p>
    </article>
  );
}

export default function CareerStatistics({ name, career = {}, seasons = [], gameStats, championshipYears = [] }) {
  const orderedSeasons = seasons.slice().sort((first, second) => second.year - first.year);
  const totals = seasons.reduce((accumulator, season) => ({
    games: accumulator.games + (season.gamesPlayed || 0),
    wins: accumulator.wins + (season.wins || 0),
    losses: accumulator.losses + (season.losses || 0),
    ties: accumulator.ties + (season.ties || 0),
    pointsFor: accumulator.pointsFor + (season.pointsFor || 0),
    pointsAgainst: accumulator.pointsAgainst + (season.pointsAgainst || 0),
  }), { games: 0, wins: 0, losses: 0, ties: 0, pointsFor: 0, pointsAgainst: 0 });

  const seasonCount = career.seasons_played || seasons.length;
  const totalGames = career.total_games || totals.games;
  const totalPointsFor = career.total_points || totals.pointsFor;
  const totalPointsAgainst = totals.pointsAgainst;
  const pointDifferential = totalPointsFor - totalPointsAgainst;
  const averagePerSeason = career.average_points_per_season
    || (seasonCount ? totalPointsFor / seasonCount : 0);
  const averagePerGame = career.average_points_per_game
    || (totalGames ? totalPointsFor / totalGames : 0);
  const averageAgainst = totalGames ? totalPointsAgainst / totalGames : 0;
  const differentialPerGame = totalGames ? pointDifferential / totalGames : 0;
  const regular = gameStats?.regular;
  const postseason = gameStats?.postseason;
  const playoffAppearances = career.playoff_appearances || 0;
  const playoffRate = career.playoff_rate || 0;
  const titleConversion = playoffAppearances
    ? (100 * championshipYears.length) / playoffAppearances
    : 0;

  return (
    <section id="career-statistics" className="px-6 md:px-14 py-14 border-b border-border bg-bg-base">
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 mb-8">
        <div>
          <Eyebrow color="gold" className="mb-2 tracking-[0.5em]">CAREER STATISTICS</Eyebrow>
          <h2 className="text-display-xs md:text-display-sm font-black tracking-normal leading-none">
            {name}'s lifetime ledger.
          </h2>
        </div>
        <div className="text-[10px] text-ink-tertiary tracking-[0.16em] italic max-w-xl xl:text-right">
          Completed regular seasons, 2013–2025 · validated standings and weekly matchup sources
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
        <Metric label="Lifetime points" value={formatNumber(totalPointsFor, 2)} note={`${formatNumber(averagePerSeason, 2)} per season`} />
        <Metric label="Weekly scoring" value={formatNumber(averagePerGame, 2)} note={`${formatNumber(regular?.medianScore || 0, 2)} median`} />
        <Metric label="Point differential" value={formatSigned(pointDifferential, 2)} note={`${formatSigned(differentialPerGame, 2)} per matchup`} tone={pointDifferential < 0 ? 'red' : 'gold'} />
        <Metric label="Career record" value={record(totals.wins, totals.losses, totals.ties)} note={`${formatNumber(career.win_percentage || 0, 1)}% win rate · ${totalGames} games`} tone="plain" />
      </div>

      <div className="mt-10">
        <Eyebrow color="gold" className="mb-4 tracking-[0.4em]">SCORING PROFILE</Eyebrow>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <Metric label="PF / season" value={formatNumber(averagePerSeason, 2)} note={`${seasonCount} seasons`} />
          <Metric label="PF / game" value={formatNumber(averagePerGame, 2)} note={`${formatSigned(career.scoring_vs_league_average || 0, 1)}% vs league average`} />
          <Metric label="PA / game" value={formatNumber(averageAgainst, 2)} note={`${formatNumber(totalPointsAgainst, 2)} lifetime PA`} tone="plain" />
          <Metric label="Score volatility" value={formatNumber(regular?.standardDeviation || 0, 2)} note="Standard deviation" tone="plain" />
          <Metric label="200+ games" value={regular?.gamesAtOrAbove200 ?? 0} note={`${regular?.gamesAtOrAbove250 ?? 0} at 250+`} />
          <Metric label="Longest streak" value={regular?.longestWinStreak ?? career.longest_win_streak ?? 0} note="Regular-season wins" />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3.5">
        <FeatureGame label="Career high" game={regular?.highScore} emptyCopy="Weekly high unavailable." />
        <FeatureGame label="Career low" game={regular?.lowScore} emptyCopy="Weekly low unavailable." />
        <FeatureGame label="Largest win" game={regular?.largestWin} emptyCopy="No regular-season win recorded." />
        <FeatureGame label="Largest loss" game={regular?.largestLoss} emptyCopy="No regular-season loss recorded." />
      </div>

      <div className="mt-10">
        <Eyebrow color="red" className="mb-4 tracking-[0.4em]">POSTSEASON RESUME</Eyebrow>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <Metric label="Playoff berths" value={playoffAppearances} note={`${formatNumber(playoffRate, 1)}% of seasons`} />
          <Metric label="Bracket record" value={record(postseason?.wins, postseason?.losses, postseason?.ties)} note="QF, SF and title games" tone="plain" />
          <Metric label="Finals" value={postseason?.finalsAppearances ?? 0} note="Championship appearances" />
          <Metric label="Championships" value={championshipYears.length} note={championshipYears.join(', ') || 'Still chasing'} />
          <Metric label="Title conversion" value={`${formatNumber(titleConversion, 1)}%`} note="Titles per playoff berth" />
          <Metric label="Playoff scoring" value={formatNumber(postseason?.averageScore || 0, 2)} note={`${postseason?.games ?? 0} played games`} tone="plain" />
        </div>
        <p className="mt-3 text-[10px] text-ink-tertiary italic">
          Bracket record excludes byes and consolation or placement games.
        </p>
      </div>

      <div className="mt-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
          <div>
            <Eyebrow color="gold" className="mb-2 tracking-[0.4em]">SEASON BY SEASON</Eyebrow>
            <h3 className="font-display text-3xl md:text-4xl font-black uppercase tracking-normal leading-none">
              Every completed campaign.
            </h3>
          </div>
          <div className="text-[10px] text-ink-tertiary tracking-[0.16em] italic">
            PF and PA are regular-season totals
          </div>
        </div>
        <div className="md:hidden mb-3 text-[10px] text-accent-gold tracking-[0.16em] font-extrabold uppercase">
          Swipe table horizontally →
        </div>
        <div className="overflow-x-auto border border-border bg-bg-surface">
          <table className="w-full min-w-[920px] text-left">
            <thead className="bg-bg-deep text-[9px] tracking-[0.2em] text-ink-tertiary uppercase">
              <tr>
                <th className="px-4 py-3">Season</th>
                <th className="px-4 py-3">Team</th>
                <th className="px-4 py-3">Record</th>
                <th className="px-4 py-3">Win %</th>
                <th className="px-4 py-3">PF</th>
                <th className="px-4 py-3">PF/G</th>
                <th className="px-4 py-3">PA</th>
                <th className="px-4 py-3">Diff</th>
                <th className="px-4 py-3">Finish</th>
              </tr>
            </thead>
            <tbody>
              {orderedSeasons.map((season) => {
                const games = season.gamesPlayed || season.wins + season.losses + season.ties;
                const winRate = games ? (100 * season.wins) / games : 0;
                const isChampion = championshipYears.includes(season.year);
                return (
                  <tr key={season.year} className="border-t border-border text-sm">
                    <td className="px-4 py-4 font-display text-xl font-black text-accent-gold">{season.year}</td>
                    <td className="px-4 py-4 max-w-[220px] font-black">{season.teamName}</td>
                    <td className="px-4 py-4 font-mono text-[12px]">{record(season.wins, season.losses, season.ties)}</td>
                    <td className="px-4 py-4 font-mono text-[12px]">{formatNumber(winRate, 1)}%</td>
                    <td className="px-4 py-4 font-mono text-[12px]">{formatNumber(season.pointsFor, 2)}</td>
                    <td className="px-4 py-4 font-mono text-[12px]">{formatNumber(games ? season.pointsFor / games : 0, 2)}</td>
                    <td className="px-4 py-4 font-mono text-[12px]">{formatNumber(season.pointsAgainst, 2)}</td>
                    <td className={`px-4 py-4 font-mono text-[12px] ${(season.pointsFor - season.pointsAgainst) >= 0 ? 'text-accent-gold' : 'text-accent-red'}`}>
                      {formatSigned(season.pointsFor - season.pointsAgainst, 2)}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-[9px] tracking-[0.16em] font-extrabold uppercase ${isChampion ? 'text-accent-gold' : season.playoffsMade ? 'text-ink-primary' : 'text-ink-tertiary'}`}>
                        {isChampion ? 'Champion' : season.playoffsMade ? 'Playoffs' : 'Missed'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
